import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Privacy = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderBottom: `1px solid ${theme.border}`,
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 10,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.accent,
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      margin: 0,
      color: theme.text,
    },
    content: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: 1.6,
    },
    lastUpdated: {
      color: theme.textMuted,
      fontSize: '14px',
      marginBottom: '24px',
    },
    text: {
      color: theme.textSecondary,
      fontSize: '14px',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '32px',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${theme.border}`,
    },
    subTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '20px',
      marginBottom: '12px',
    },
    bold: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    list: {
      color: theme.textSecondary,
      fontSize: '14px',
      paddingLeft: '24px',
      marginBottom: '16px',
    },
    highlight: {
      color: '#22c55e',
      fontSize: '14px',
      fontWeight: '600',
      padding: '12px 16px',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    finalText: {
      color: theme.textMuted,
      fontSize: '13px',
      marginTop: '32px',
      paddingTop: '16px',
      borderTop: `1px solid ${theme.border}`,
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={styles.title}>Privacy Policy</h1>
      </div>

      <div style={styles.content}>
        <p style={styles.lastUpdated}>Last Updated: January 11, 2025</p>

        <p style={styles.text}>
          This Privacy Policy explains how CardChase ("we," "us," or "our") collects, uses, and protects your information when you use our mobile application.
        </p>
        <p style={styles.text}>
          By using CardChase, you agree to the collection and use of information as described in this policy.
        </p>

        <h2 style={styles.sectionTitle}>1. Information We Collect</h2>

        <h3 style={styles.subTitle}>Information You Provide</h3>
        <p style={styles.bold}>Account Information:</p>
        <ul style={styles.list}>
          <li>Name (first and last)</li>
          <li>Email address</li>
          <li>Username</li>
          <li>Password</li>
          <li>Profile photo (optional)</li>
        </ul>

        <p style={styles.bold}>Collection Data:</p>
        <ul style={styles.list}>
          <li>Cards you add to your collection</li>
          <li>Binders you create</li>
          <li>Card conditions and grades</li>
          <li>Grading company information</li>
        </ul>

        <p style={styles.bold}>Activity Data:</p>
        <ul style={styles.list}>
          <li>Posts you create</li>
          <li>Trade requests you send and receive</li>
          <li>Messages you send to other users</li>
          <li>Ratings and reviews you give</li>
          <li>Users and locations you follow</li>
        </ul>

        <h3 style={styles.subTitle}>Information Collected Automatically</h3>
        <p style={styles.bold}>Location Data:</p>
        <ul style={styles.list}>
          <li>Your geographic location (latitude and longitude)</li>
          <li>Used to calculate distances between users and to locations</li>
          <li>We only display approximate distances (minimum 5 miles shown)</li>
          <li>We NEVER share your exact location with other users</li>
        </ul>

        <p style={styles.bold}>Device Information:</p>
        <ul style={styles.list}>
          <li>Device type and operating system</li>
          <li>Push notification tokens</li>
          <li>Device identifiers for notifications</li>
        </ul>

        <h2 style={styles.sectionTitle}>2. How We Use Your Information</h2>
        <p style={styles.text}>We use your information to:</p>
        <ul style={styles.list}>
          <li>Create and manage your account</li>
          <li>Display your collection and posts</li>
          <li>Enable trading and messaging between users</li>
          <li>Show distances to other users and locations</li>
          <li>Send push notifications about trades, messages, and activity</li>
          <li>Improve the service and fix bugs</li>
          <li>Detect and prevent fraud</li>
          <li>Enforce our Terms of Service</li>
        </ul>

        <h2 style={styles.sectionTitle}>3. How We Share Your Information</h2>

        <h3 style={styles.subTitle}>With Other Users</h3>
        <p style={styles.text}>Other users can see:</p>
        <ul style={styles.list}>
          <li>Your username and profile photo</li>
          <li>Your posts and card listings</li>
          <li>Your trader rating</li>
          <li>Your approximate distance (not exact location)</li>
          <li>Messages you send them directly</li>
        </ul>

        <p style={styles.text}>Other users CANNOT see:</p>
        <ul style={styles.list}>
          <li>Your exact location or address</li>
          <li>Your email address</li>
          <li>Your real name (unless you choose to share it)</li>
        </ul>

        <h3 style={styles.subTitle}>With Service Providers</h3>
        <ul style={styles.list}>
          <li><strong>OneSignal:</strong> Push notification delivery</li>
          <li><strong>Supabase:</strong> Database and authentication services</li>
          <li><strong>Hosting providers:</strong> App infrastructure</li>
        </ul>

        <h3 style={styles.subTitle}>We Do NOT Sell Your Information</h3>
        <p style={styles.highlight}>CardChase does not sell your personal information to third parties.</p>

        <h2 style={styles.sectionTitle}>4. Children's Privacy (COPPA Compliance)</h2>
        <p style={styles.text}>CardChase takes children's privacy seriously.</p>

        <h3 style={styles.subTitle}>Age Requirements</h3>
        <ul style={styles.list}>
          <li>Users 13 and older can create their own accounts</li>
          <li>Users under 13 MUST use CardChase through a parent/guardian account</li>
        </ul>

        <h3 style={styles.subTitle}>Parental Rights</h3>
        <p style={styles.text}>Parents/guardians can:</p>
        <ul style={styles.list}>
          <li>Access their child's account data</li>
          <li>Review their child's activity</li>
          <li>Request deletion of their child's data</li>
          <li>Restrict trading features</li>
        </ul>

        <p style={styles.text}>
          If you believe we have collected information from a child under 13 without parental consent, contact us immediately.
        </p>

        <h2 style={styles.sectionTitle}>5. California Privacy Rights (CCPA)</h2>
        <p style={styles.text}>If you are a California resident, you have additional rights:</p>
        <ul style={styles.list}>
          <li><strong>Right to Know:</strong> What personal information we collect and how we use it</li>
          <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
          <li><strong>Right to Opt-Out:</strong> We do NOT sell personal information</li>
          <li><strong>Right to Non-Discrimination:</strong> We won't discriminate for exercising your rights</li>
        </ul>

        <p style={styles.text}>To make a CCPA request, email tradersupport@cardchase.org</p>

        <h2 style={styles.sectionTitle}>6. Data Security</h2>
        <p style={styles.text}>We implement reasonable security measures including:</p>
        <ul style={styles.list}>
          <li>Encrypted data transmission (HTTPS)</li>
          <li>Secure database storage</li>
          <li>Access controls and authentication</li>
        </ul>
        <p style={styles.text}>
          <strong>However, no system is 100% secure.</strong> We cannot guarantee absolute security of your information.
        </p>

        <h2 style={styles.sectionTitle}>7. Data Retention</h2>
        <ul style={styles.list}>
          <li><strong>Account data:</strong> Until you delete your account</li>
          <li><strong>Messages:</strong> Retained while your account is active</li>
          <li><strong>Location data:</strong> Collected when app is in use, used to calculate distances</li>
        </ul>

        <h3 style={styles.subTitle}>Account Deletion</h3>
        <p style={styles.text}>
          You can delete your account in app settings. Your profile and posts will be removed. Some data may be retained for legal/business purposes.
        </p>

        <h2 style={styles.sectionTitle}>8. Third-Party Services</h2>
        <p style={styles.text}>CardChase uses third-party services with their own privacy policies:</p>
        <ul style={styles.list}>
          <li>OneSignal (push notifications)</li>
          <li>Supabase (backend services)</li>
          <li>eBay API (card pricing data for Chef's Lab)</li>
        </ul>

        <h2 style={styles.sectionTitle}>9. Your Choices</h2>
        <ul style={styles.list}>
          <li><strong>Push Notifications:</strong> Disable in device settings</li>
          <li><strong>Location Data:</strong> Required for core features; disable in device settings (limits functionality)</li>
          <li><strong>Account Visibility:</strong> Hide your posts or profile from other users</li>
        </ul>

        <h2 style={styles.sectionTitle}>10. Changes to This Policy</h2>
        <p style={styles.text}>
          We may update this Privacy Policy. We will notify you of material changes through in-app notifications or email.
        </p>

        <h2 style={styles.sectionTitle}>11. Contact Us</h2>
        <p style={styles.text}>
          <strong>CardChase</strong><br />
          Email: tradersupport@cardchase.org<br />
          Address: Trainer Center, 4911 Warner Ave #210, Huntington Beach, CA 92649
        </p>
        <p style={styles.text}>
          For privacy requests, CCPA requests, or children's privacy concerns, email tradersupport@cardchase.org
        </p>

        <p style={styles.finalText}>
          By using CardChase, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
